import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import { useWorkshopSchedule } from "../hooks/useWorkshopSchedule";

// Special offer countdown - 40 minutes from page load
const OFFER_DURATION = 40 * 60 * 1000; // 40 minutes in milliseconds

function useOfferCountdown() {
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedEndTime = localStorage.getItem('offerEndTime');
    if (savedEndTime) {
      const endTime = parseInt(savedEndTime);
      const remaining = Math.max(0, endTime - Date.now());
      if (remaining > 0) {
        return getOfferTimeParts(remaining);
      }
    }
    
    // Start new countdown
    const endTime = Date.now() + OFFER_DURATION;
    localStorage.setItem('offerEndTime', endTime.toString());
    return getOfferTimeParts(OFFER_DURATION);
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const savedEndTime = localStorage.getItem('offerEndTime');
      if (savedEndTime) {
        const endTime = parseInt(savedEndTime);
        const remaining = Math.max(0, endTime - Date.now());
        
        if (remaining === 0) {
          // Restart countdown
          const newEndTime = Date.now() + OFFER_DURATION;
          localStorage.setItem('offerEndTime', newEndTime.toString());
          setTimeLeft(getOfferTimeParts(OFFER_DURATION));
        } else {
          setTimeLeft(getOfferTimeParts(remaining));
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

function getOfferTimeParts(milliseconds) {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds };
}

export default function HeroSection({ openRegistration = () => {} }) {
  const { workshopDetails } = useWorkshopSchedule();
  const offerTimeLeft = useOfferCountdown();

  const handleRegisterClick = () => {
    // Track Facebook Pixel event
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_name: 'Free Sales Automation Webinar - Daily Session',
        content_category: 'Webinar',
        value: 0,
        currency: 'INR'
      });
    }
    openRegistration(true);
  };

  return (
    <header className="hero-container">
      {/* Top thin red bar - Updated for free webinar */}
      <div className="hero-top-bar">
        FREE Daily Live Sales Automation Webinar - Turn Your Business Into a Money-Making Machine
      </div>

      {/* Hero area */}
      <section className="hero-section">
        <div className="hero-inner">
          {/* Headline Area - Centered above columns */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="hero-headline-area"
          >
            {/* Subheading */}
            <motion.div
              className="hero-subheading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              ATTENTION: Coaches, Consultants & Service Providers!
            </motion.div>

            {/* Decorative rule */}
            <motion.div
              className="hero-rule"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            ></motion.div>

            {/* Big headline - Updated for free webinar */}
            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              🚀 Stop Wasting Money on Ads That Don't Bring Real Clients!<br />
              <span className="highlight">Turn Confusion Into ₹3+ Lakhs</span> Monthly Revenue{" "}
              <span className="subtitle-text">with Our Proven Step-by-Step System...</span>
            </motion.h1>
          </motion.div>

          {/* Two-column layout */}
          <div className="hero-columns">
            {/* LEFT: Image Card */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hero-left-column"
            >
              <motion.div
                className="hero-image-card"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.02 }}
              >
                <img
                  src="https://storage.files-vault.com/uploads/1760945103-H0PweDnHcx.webp"
                  alt="Sales Automation Webinar Success Stories"
                  width="520"
                  height="400"
                  loading="eager"
                />
              </motion.div>
            </motion.div>

            {/* RIGHT: Offer Panel */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hero-right-column"
            >
              <div className="offer-panel shine-animation">
                <motion.div
                  className="offer-header"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <FiCheck className="offer-check" />
                  <span>
                    Join the FREE DAILY LIVE Webinar & Get Personal Guidance <br />
                    + 1-on-1 Mentorship Absolutely FREE! 🆓
                  </span>
                </motion.div>

                <div className="offer-expiry">
                  <div className="expiry-text">Limited Seats Available...</div>
                  <motion.div
                    className="countdown-display"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number"
                        key={offerTimeLeft.days}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.days || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Days</div>
                    </div>
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number"
                        key={offerTimeLeft.hours}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.hours || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Hours</div>
                    </div>
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number"
                        key={offerTimeLeft.minutes}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.minutes || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Minutes</div>
                    </div>
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number pulse-animation"
                        key={offerTimeLeft.seconds}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.seconds || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Seconds</div>
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  className="offer-time"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  {/* Dynamic webinar timing using the auto-update system */}
                  Next Session: {workshopDetails.formattedDate} - {workshopDetails.timeString}
                </motion.div>

                <motion.button
                  onClick={handleRegisterClick}
                  className="offer-cta free-cta"
                  aria-haspopup="dialog"
                  aria-label="Register Now - Completely FREE"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  🎯 Register Now - Completely FREE
                </motion.button>

                <motion.div
                  className="offer-bonus"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.4 }}
                >
                  <div className="bonus-text">
                    <span className="flame-icon">🔥</span>
                    Limited Seats – Only 50 People Per Session!
                  </div>
                  <div className="progress-container">
                    <div className="progress-bar">
                      <motion.div
                        className="progress-fill"
                        initial={{ width: "0%" }}
                        animate={{ width: "65%" }}
                        transition={{ duration: 2, delay: 1.6 }}
                      ></motion.div>
                    </div>
                    <div className="progress-text">
                      65% Seats Already Booked • Get Rs.10 Lakh Worth of Bonus for FREE
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </header>
  );
}